import * as React from "react";
import { X, Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * MultiSelect - A fancy multi-select dropdown with tag display
 * 
 * @param {Array} options - Array of {value, label} objects for dropdown options
 * @param {Array} selected - Array of selected values
 * @param {Function} onChange - Callback when selection changes
 * @param {String} placeholder - Placeholder text
 * @param {Boolean} allowCustom - Allow adding custom values not in options
 * @param {String} className - Additional CSS classes
 */
export function MultiSelect({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select items...",
  allowCustom = true,
  className,
  maxDisplayed = 10,
  tagVariant = "default", // default, outline, secondary
}) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const handleUnselect = (item) => {
    onChange(selected.filter((s) => s !== item));
  };

  const handleSelect = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
    setInputValue("");
  };

  const handleAddCustom = () => {
    if (inputValue.trim() && !selected.includes(inputValue.trim())) {
      onChange([...selected, inputValue.trim()]);
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && allowCustom && inputValue.trim()) {
      e.preventDefault();
      handleAddCustom();
    }
    if (e.key === "Backspace" && !inputValue && selected.length > 0) {
      handleUnselect(selected[selected.length - 1]);
    }
  };

  const filteredOptions = options.filter(
    (option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.value.toLowerCase().includes(inputValue.toLowerCase())
  );

  const getTagStyles = () => {
    switch (tagVariant) {
      case "outline":
        return "bg-transparent border-2 border-primary text-primary hover:bg-primary/10";
      case "secondary":
        return "bg-secondary text-secondary-foreground hover:bg-secondary/80";
      default:
        return "bg-primary text-primary-foreground hover:bg-primary/80";
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between min-h-[42px] h-auto py-2 px-3 font-normal"
          >
            <span className="text-muted-foreground truncate">
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full min-w-[300px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search or type to add..."
              value={inputValue}
              onValueChange={setInputValue}
              onKeyDown={handleKeyDown}
            />
            <CommandList>
              <CommandEmpty>
                {allowCustom && inputValue.trim() ? (
                  <button
                    onClick={handleAddCustom}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-accent rounded cursor-pointer"
                  >
                    <Plus className="h-4 w-4" />
                    Add "{inputValue.trim()}"
                  </button>
                ) : (
                  <span className="text-muted-foreground px-4 py-2 block">No options found.</span>
                )}
              </CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handleSelect(option.value)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selected.includes(option.value) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Tags Display */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50/50 dark:bg-slate-800/50 min-h-[48px]">
          {selected.slice(0, maxDisplayed).map((item) => (
            <Badge
              key={item}
              className={cn(
                "px-2.5 py-1 text-xs font-medium cursor-pointer transition-all",
                "flex items-center gap-1.5 rounded-md",
                getTagStyles()
              )}
              onClick={() => handleUnselect(item)}
            >
              {item}
              <X className="h-3 w-3 opacity-70 hover:opacity-100" />
            </Badge>
          ))}
          {selected.length > maxDisplayed && (
            <Badge variant="outline" className="px-2.5 py-1 text-xs">
              +{selected.length - maxDisplayed} more
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * TagInput - Simple tag input without dropdown
 * Just type and press Enter to add tags
 */
export function TagInput({
  value = [],
  onChange,
  placeholder = "Type and press Enter...",
  className,
  tagVariant = "default",
}) {
  const [inputValue, setInputValue] = React.useState("");
  const inputRef = React.useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      if (!value.includes(inputValue.trim())) {
        onChange([...value, inputValue.trim()]);
      }
      setInputValue("");
    }
    if (e.key === "Backspace" && !inputValue && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handleRemove = (tag) => {
    onChange(value.filter((t) => t !== tag));
  };

  const getTagStyles = () => {
    switch (tagVariant) {
      case "outline":
        return "bg-transparent border-2 border-blue-500 text-blue-600 hover:bg-blue-50";
      case "secondary":
        return "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200";
      case "colorful":
        return "bg-gradient-to-r from-blue-500 to-purple-500 text-white";
      default:
        return "bg-blue-500 text-white hover:bg-blue-600";
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div 
        className="flex flex-wrap items-center gap-1.5 p-2 border rounded-lg bg-white dark:bg-slate-900 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 min-h-[48px] cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <Badge
            key={tag}
            className={cn(
              "px-2.5 py-1 text-xs font-medium cursor-pointer transition-all",
              "flex items-center gap-1.5 rounded-md shadow-sm",
              getTagStyles()
            )}
            onClick={(e) => {
              e.stopPropagation();
              handleRemove(tag);
            }}
          >
            {tag}
            <X className="h-3 w-3 opacity-70 hover:opacity-100" />
          </Badge>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] bg-transparent border-0 outline-none text-sm placeholder:text-slate-400"
        />
      </div>
      {value.length > 0 && (
        <p className="text-xs text-slate-500">
          Click on a tag to remove it. Press Backspace to remove the last tag.
        </p>
      )}
    </div>
  );
}

export default MultiSelect;
